import { inject, Injectable, NgZone } from '@angular/core';
import { Observable, Subject } from 'rxjs';
import {
  AssetVerificationEvent,
  AssetVerificationEventType,
  AssetVerificationLaunchOptions,
  AssetVerificationProvider,
} from './asset-verification-provider.interface';

declare global {
  interface Window {
    FormFree?: {
      AccountChek?: {
        init: (config: FormfreeConfig) => void;
        open: () => void;
        close: () => void;
      };
    };
  }
}

interface FormfreeConfig {
  url: string;
  containerId?: string;
  onSuccess?: (data: unknown) => void;
  onCancel?: () => void;
  onError?: (error: unknown) => void;
  onLoad?: () => void;
}

@Injectable({
  providedIn: 'root',
})
export class FormfreeConnectService implements AssetVerificationProvider {
  private readonly ngZone = inject(NgZone);
  private readonly events$ = new Subject<AssetVerificationEvent>();
  private isActive = false;
  private iframeContainer: HTMLDivElement | null = null;
  private iframe: HTMLIFrameElement | null = null;
  private messageListener: ((event: MessageEvent) => void) | null = null;

  isLoaded(): boolean {
    return true;
  }

  isSdkLoaded(): boolean {
    return typeof window.FormFree?.AccountChek !== 'undefined';
  }

  launch(url: string, options: AssetVerificationLaunchOptions = {}): Observable<AssetVerificationEvent> {
    if (this.isActive) {
      this.destroy();
    }

    this.isActive = true;

    if (this.isSdkLoaded()) {
      this.launchWithSdk(url, options);
    } else {
      this.launchWithIframe(url, options);
    }

    return this.events$.asObservable();
  }

  private launchWithSdk(url: string, options: AssetVerificationLaunchOptions): void {
    const containerId = options.selector?.replace('#', '') || 'formfree-container';

    window.FormFree?.AccountChek?.init({
      url,
      containerId,
      onSuccess: (data: unknown) => {
        this.ngZone.run(() => {
          this.events$.next({ type: 'success', data });
          this.isActive = false;
        });
      },
      onCancel: () => {
        this.ngZone.run(() => {
          this.events$.next({ type: 'cancel' });
          this.isActive = false;
        });
      },
      onError: (error: unknown) => {
        this.ngZone.run(() => {
          this.events$.next({ type: 'error', data: error });
          this.isActive = false;
        });
      },
      onLoad: () => {
        this.ngZone.run(() => {
          this.events$.next({ type: 'loaded' });
        });
      },
    });

    window.FormFree?.AccountChek?.open();
  }

  private launchWithIframe(url: string, options: AssetVerificationLaunchOptions): void {
    this.iframeContainer = document.createElement('div');
    this.iframeContainer.id = 'formfree-overlay';
    this.iframeContainer.style.cssText = `
      position: fixed;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      background: ${options.overlay || 'rgba(0, 0, 0, 0.5)'};
      z-index: 10000;
      display: flex;
      align-items: center;
      justify-content: center;
    `;

    const iframeWrapper = document.createElement('div');
    iframeWrapper.style.cssText = `
      background: white;
      border-radius: 8px;
      width: 90%;
      max-width: 600px;
      height: 80%;
      max-height: 700px;
      position: relative;
      overflow: hidden;
    `;

    const closeButton = document.createElement('button');
    closeButton.innerHTML = '&times;';
    closeButton.style.cssText = `
      position: absolute;
      top: 10px;
      right: 10px;
      background: none;
      border: none;
      font-size: 24px;
      cursor: pointer;
      z-index: 1;
      color: #666;
    `;
    closeButton.onclick = () => {
      this.ngZone.run(() => {
        this.events$.next({ type: 'cancel' });
        this.destroy();
      });
    };

    this.iframe = document.createElement('iframe');
    this.iframe.src = url;
    this.iframe.style.cssText = `
      width: 100%;
      height: 100%;
      border: none;
    `;

    this.iframe.onload = () => {
      this.ngZone.run(() => {
        this.events$.next({ type: 'loaded' });
      });
    };

    this.messageListener = (event: MessageEvent) => {
      if (this.isFormfreeMessage(event)) {
        this.handleFormfreeMessage(event.data);
      }
    };
    window.addEventListener('message', this.messageListener);

    iframeWrapper.appendChild(closeButton);
    iframeWrapper.appendChild(this.iframe);
    this.iframeContainer.appendChild(iframeWrapper);
    document.body.appendChild(this.iframeContainer);
  }

  private isFormfreeMessage(event: MessageEvent): boolean {
    const data = event.data;
    if (typeof data === 'object' && data !== null) {
      return (
        'formfree' in data ||
        'accountchek' in data ||
        data.type?.toLowerCase().includes('formfree') ||
        data.source?.toLowerCase().includes('formfree')
      );
    }
    if (typeof data === 'string') {
      try {
        const parsed = JSON.parse(data);
        return parsed.formfree || parsed.accountchek || parsed.type?.toLowerCase().includes('formfree');
      } catch {
        return data.toLowerCase().includes('formfree');
      }
    }
    return false;
  }

  private handleFormfreeMessage(data: unknown): void {
    this.ngZone.run(() => {
      const messageData = typeof data === 'string' ? JSON.parse(data) : data;
      const eventType = this.mapFormfreeEventType(messageData);

      this.events$.next({ type: eventType, data: messageData });

      if (eventType === 'success' || eventType === 'cancel' || eventType === 'error') {
        this.destroy();
      }
    });
  }

  private mapFormfreeEventType(data: Record<string, unknown>): AssetVerificationEventType {
    const status = (data['status'] as string)?.toLowerCase() || '';
    const type = (data['type'] as string)?.toLowerCase() || '';
    const event = (data['event'] as string)?.toLowerCase() || '';

    if (status === 'success' || type === 'success' || event === 'success' || event === 'complete') {
      return 'success';
    }
    if (status === 'cancel' || type === 'cancel' || event === 'cancel' || event === 'close') {
      return 'cancel';
    }
    if (status === 'error' || type === 'error' || event === 'error') {
      return 'error';
    }
    return 'user';
  }

  destroy(): void {
    if (this.isSdkLoaded() && this.isActive) {
      try {
        window.FormFree?.AccountChek?.close();
      } catch {
        // Ignore errors during cleanup
      }
    }

    if (this.iframeContainer) {
      document.body.removeChild(this.iframeContainer);
      this.iframeContainer = null;
      this.iframe = null;
    }

    if (this.messageListener) {
      window.removeEventListener('message', this.messageListener);
      this.messageListener = null;
    }

    this.isActive = false;
  }

  getEvents(): Observable<AssetVerificationEvent> {
    return this.events$.asObservable();
  }
}
